import React, { FC, memo, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  colors,
  FormControlLabel,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { AlternatingColorTableRow } from 'src/components';
import { AmountCell, MOPCell } from './table';
import useCustomerPayment from 'src/hooks/customer-payment/use-customer-payment';
import { Pagination } from '@material-ui/lab';
import { CommonCustomerPaymentPayload } from 'src/redux/slices/customer-payment';
import { cleanUrlParams } from 'src/utils';
import { useNavigate } from 'react-router';

const headers = [
  'Date Sold',
  'Payment Date',
  'Branch',
  'Customer',
  'SI No.',
  'MOP',
  'Amount'
];

const useStyles = makeStyles(() => ({
  imgPreview: {
    width: undefined,
    height: 50
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'left',
    borderBottom: '1px solid #a4abba'
  },
  tableCellAction: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

interface Props {}

const Component: FC<Props> = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    customerPaymentList,
    customerPaymentParams,
    setParams,
    fetchList
  } = useCustomerPayment();

  const meta = customerPaymentList?.meta;
  const paperRef = React.useRef<HTMLDivElement>(null);
  const [showWithBalanceOnly, setShowWithBalanceOnly] = useState<boolean>(
    false
  );

  const filteredList = useMemo(() => {
    if (showWithBalanceOnly) {
      const filteredList = customerPaymentList?.list?.filter((data) =>
        data?.balance ? data?.balance > 0 : false
      );

      return filteredList;
    }
    return customerPaymentList?.list;
  }, [customerPaymentList.list, showWithBalanceOnly]);

  const onHandlePageChange = (params: CommonCustomerPaymentPayload) => {
    const cleanParams = cleanUrlParams(params);
    setParams(params);
    fetchList(params);
    navigate(`?${cleanParams.toString()}`);

    //scroll to top
    setTimeout(() => {
      paperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  return (
    <>
      <Paper ref={paperRef}>
        {customerPaymentList?.is_loading ? <LinearProgress /> : null}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            padding: 25
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showWithBalanceOnly}
                onChange={(e) => {
                  setShowWithBalanceOnly(e.target.checked);
                }}
                color="primary"
              />
            }
            label="Show Payments With Balance Only"
          />
          <Pagination
            disabled={customerPaymentList?.is_loading}
            count={meta?.last_page || 1}
            page={Number(customerPaymentParams?.page) || 1}
            onChange={(e, p) =>
              onHandlePageChange({ ...customerPaymentParams, page: p })
            }
          />
        </Box>
        {filteredList?.length > 0 ? (
          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {headers?.map((header) => (
                    <TableCell className={classes.tableCell} key={header}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredList?.map((payment, index) => (
                  <AlternatingColorTableRow
                    key={`${payment?.transaction_no}-${index}`}
                  >
                    <TableCell>{payment?.date_sold}</TableCell>
                    <TableCell>
                      <Typography style={{ whiteSpace: 'pre-line' }}>
                        {payment?.payments
                          ?.map((p) => p.payment_date)
                          .join('\n')}
                      </Typography>
                    </TableCell>
                    <TableCell>{payment?.branch}</TableCell>
                    <TableCell>{payment?.customer}</TableCell>
                    <TableCell>{payment?.si_no}</TableCell>
                    <TableCell>
                      <MOPCell data={payment?.payments || []} />
                    </TableCell>
                    <TableCell>
                      <AmountCell
                        data={{
                          total_retail_price: payment?.total_retail_price || 0,
                          amount_paid: payment?.amount_paid || 0,
                          balance: payment?.balance || 0,
                          total_payments: payment?.total_payments || 0,
                          total_sales: payment?.total_sales || 0
                        }}
                      />
                    </TableCell>
                  </AlternatingColorTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '20vh'
            }}
          >
            <Typography>--- Nothing to Show ---</Typography>
          </div>
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 25
          }}
        >
          <Pagination
            disabled={customerPaymentList?.is_loading}
            count={meta?.last_page || 1}
            page={Number(customerPaymentParams?.page) || 1}
            onChange={(e, p) =>
              onHandlePageChange({ ...customerPaymentParams, page: p })
            }
          />
        </Box>
      </Paper>
    </>
  );
};

export const PaymentList = memo(Component);
